import { useState, useEffect } from 'react';
import { 
  Database, Shield, AlertTriangle, CheckCircle, RefreshCw, 
  Search, FileText, Users, Wallet, TrendingUp, Bell, 
  Clock, ChevronDown, ChevronUp, ExternalLink, XCircle,
  Activity, Server, HardDrive
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const SystemAudit = () => {
  const [auditReport, setAuditReport] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [orphanedRefs, setOrphanedRefs] = useState(null);
  const [entitySearch, setEntitySearch] = useState('');
  const [entityRelationships, setEntityRelationships] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    statistics: true,
    validation: false,
    orphaned: false,
    search: false
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const response = await api.get('/admin/audit/statistics');
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      toast.error('Failed to load database statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const runFullAudit = async () => {
    try {
      setLoading(true);
      toast.info('Running database audit...');
      const response = await api.get('/admin/audit/database');
      setAuditReport(response.data.report);
      setExpandedSections(prev => ({ ...prev, validation: true }));
      toast.success('Database audit completed');
    } catch (error) {
      console.error('Audit failed:', error);
      toast.error('Failed to run database audit');
    } finally {
      setLoading(false);
    }
  };

  const checkOrphanedReferences = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/audit/orphaned-references');
      setOrphanedRefs(response.data);
      setExpandedSections(prev => ({ ...prev, orphaned: true }));
      toast.success(`Found ${response.data.count} orphaned references`);
    } catch (error) {
      console.error('Orphaned check failed:', error);
      toast.error('Failed to check orphaned references');
    } finally {
      setLoading(false);
    }
  };

  const searchEntity = async () => {
    if (!entitySearch.trim()) {
      toast.error('Please enter an entity ID to search');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/admin/audit/entity/${entitySearch.trim()}`);
      setEntityRelationships(response.data.relationships);
      setExpandedSections(prev => ({ ...prev, search: true }));
      toast.success('Entity relationships found');
    } catch (error) {
      console.error('Entity search failed:', error);
      toast.error('Failed to search entity');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCollectionIcon = (collection) => {
    const icons = {
      users: Users,
      deposits: Wallet,
      withdrawals: Wallet,
      binary_trades: TrendingUp,
      notifications: Bell,
      audit_logs: FileText,
      portfolios: Activity,
      arbitrage_investments: TrendingUp,
      wallets: Wallet,
      kyc_submissions: Shield
    };
    return icons[collection] || Database;
  };

  const getHealthStatus = () => {
    if (!auditReport) return null;
    const { issues_found, invalid_formats, orphaned_references } = auditReport.summary;
    const totalIssues = issues_found + invalid_formats + orphaned_references;
    
    if (totalIssues === 0) return { status: 'healthy', color: 'green', icon: CheckCircle };
    if (totalIssues < 5) return { status: 'warning', color: 'yellow', icon: AlertTriangle };
    return { status: 'critical', color: 'red', icon: XCircle };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Database className="h-8 w-8 text-purple-400" />
            System Audit
          </h1>
          <p className="text-gray-400 mt-1">Database integrity and ID validation reports</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchStatistics}
            disabled={loadingStats}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
            Refresh Stats
          </Button>
          <Button
            onClick={runFullAudit}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Shield className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Run Full Audit
          </Button>
        </div>
      </div>

      {/* Health Status Banner */}
      {healthStatus && (
        <div className={`p-4 rounded-xl border ${
          healthStatus.color === 'green' ? 'bg-green-500/10 border-green-500/30' :
          healthStatus.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' :
          'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center gap-3">
            <healthStatus.icon className={`h-6 w-6 ${
              healthStatus.color === 'green' ? 'text-green-400' :
              healthStatus.color === 'yellow' ? 'text-yellow-400' :
              'text-red-400'
            }`} />
            <div>
              <h3 className={`font-semibold ${
                healthStatus.color === 'green' ? 'text-green-400' :
                healthStatus.color === 'yellow' ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                Database Health: {healthStatus.status.charAt(0).toUpperCase() + healthStatus.status.slice(1)}
              </h3>
              <p className="text-sm text-gray-400">
                {auditReport.summary.issues_found} issues, {auditReport.summary.invalid_formats} invalid formats, {auditReport.summary.orphaned_references} orphaned refs
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Statistics */}
      <div 
        className="glow-card rounded-xl overflow-hidden cursor-pointer"
        onClick={() => toggleSection('statistics')}
      >
        <div className="p-4 flex items-center justify-between bg-gray-800/50">
          <div className="flex items-center gap-3">
            <Server className="h-5 w-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Database Statistics</h2>
          </div>
          {expandedSections.statistics ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </div>
        
        {expandedSections.statistics && (
          <div className="p-4 border-t border-gray-700">
            {loadingStats ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-8 w-8 text-purple-400 animate-spin" />
              </div>
            ) : statistics ? (
              <div className="space-y-4">
                {/* Total Summary */}
                <div className="flex items-center gap-4 p-4 bg-purple-600/10 border border-purple-500/30 rounded-xl">
                  <HardDrive className="h-10 w-10 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{statistics.total_entities?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-400">Total Entities</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-sm text-gray-400">Last Updated</p>
                    <p className="text-white">{new Date(statistics.timestamp).toLocaleString()}</p>
                  </div>
                </div>

                {/* Collection Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {statistics.collections && Object.entries(statistics.collections).map(([collection, count]) => {
                    const Icon = getCollectionIcon(collection);
                    return (
                      <div 
                        key={collection} 
                        className="p-3 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-purple-500/50 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-4 w-4 text-purple-400" />
                          <span className="text-xs text-gray-400 truncate capitalize">{collection.replace('_', ' ')}</span>
                        </div>
                        <p className="text-xl font-bold text-white">{count.toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-400 py-4">No statistics available</p>
            )}
          </div>
        )}
      </div>

      {/* Validation Report */}
      <div 
        className="glow-card rounded-xl overflow-hidden cursor-pointer"
        onClick={() => toggleSection('validation')}
      >
        <div className="p-4 flex items-center justify-between bg-gray-800/50">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-green-400" />
            <h2 className="text-lg font-semibold text-white">ID Validation Report</h2>
            {auditReport && (
              <span className={`px-2 py-1 text-xs rounded-full ${
                auditReport.summary.issues_found === 0 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {auditReport.summary.issues_found} issues
              </span>
            )}
          </div>
          {expandedSections.validation ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </div>
        
        {expandedSections.validation && (
          <div className="p-4 border-t border-gray-700">
            {!auditReport ? (
              <div className="text-center py-8">
                <Shield className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">Click &quot;Run Full Audit&quot; to generate a validation report</p>
                <Button onClick={runFullAudit} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
                  Run Audit
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <p className="text-xs text-gray-400">Total Entities</p>
                    <p className="text-xl font-bold text-white">{auditReport.summary.total_entities}</p>
                  </div>
                  <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <p className="text-xs text-gray-400">Collections</p>
                    <p className="text-xl font-bold text-white">{auditReport.summary.total_collections}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${auditReport.summary.issues_found > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                    <p className="text-xs text-gray-400">Issues</p>
                    <p className={`text-xl font-bold ${auditReport.summary.issues_found > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {auditReport.summary.issues_found}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${auditReport.summary.invalid_formats > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                    <p className="text-xs text-gray-400">Invalid IDs</p>
                    <p className={`text-xl font-bold ${auditReport.summary.invalid_formats > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {auditReport.summary.invalid_formats}
                    </p>
                  </div>
                </div>

                {/* Issues List */}
                {auditReport.validation?.issues?.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-2">Issues Found</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {auditReport.validation.issues.map((issue, index) => (
                        <div key={index} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm">
                          <span className="text-yellow-400 font-semibold">{issue.collection}</span>
                          <span className="text-gray-400"> / {issue.key}: </span>
                          <span className="text-gray-300">{issue.issue || issue.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invalid Formats */}
                {auditReport.validation?.invalid_formats?.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-2">Invalid ID Formats</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {auditReport.validation.invalid_formats.map((item, index) => (
                        <div key={index} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
                          <span className="text-red-400 font-semibold">{item.collection}</span>
                          <span className="text-gray-400"> / ID: </span>
                          <code className="text-gray-300 bg-gray-800 px-1 rounded">{item.id}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  Report generated: {new Date(auditReport.generated_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Orphaned References */}
      <div 
        className="glow-card rounded-xl overflow-hidden cursor-pointer"
        onClick={() => toggleSection('orphaned')}
      >
        <div className="p-4 flex items-center justify-between bg-gray-800/50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <h2 className="text-lg font-semibold text-white">Orphaned References</h2>
            {orphanedRefs && (
              <span className={`px-2 py-1 text-xs rounded-full ${
                orphanedRefs.count === 0 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {orphanedRefs.count} found
              </span>
            )}
          </div>
          {expandedSections.orphaned ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </div>
        
        {expandedSections.orphaned && (
          <div className="p-4 border-t border-gray-700">
            {!orphanedRefs ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">Check for orphaned references (records pointing to deleted entities)</p>
                <Button onClick={checkOrphanedReferences} disabled={loading} className="bg-yellow-600 hover:bg-yellow-700">
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Check Orphaned
                </Button>
              </div>
            ) : orphanedRefs.count === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <p className="text-green-400 font-semibold">No orphaned references found!</p>
                <p className="text-gray-400 text-sm">All foreign key references are valid</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {orphanedRefs.orphaned_references.map((orphan, index) => (
                  <div key={index} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm">
                          <span className="text-yellow-400 font-semibold capitalize">{orphan.collection}</span>
                          <span className="text-gray-400"> / </span>
                          <code className="text-gray-300 text-xs bg-gray-800 px-1 rounded">{orphan.entity_id}</code>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          References missing {orphan.referenced_collection}: 
                          <code className="text-red-400 ml-1">{orphan.referenced_id}</code>
                        </p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-gray-800 text-gray-400 rounded">{orphan.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Entity Relationship Search */}
      <div 
        className="glow-card rounded-xl overflow-hidden cursor-pointer"
        onClick={() => toggleSection('search')}
      >
        <div className="p-4 flex items-center justify-between bg-gray-800/50">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Entity Relationship Search</h2>
          </div>
          {expandedSections.search ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </div>
        
        {expandedSections.search && (
          <div className="p-4 border-t border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-2 mb-4">
              <Input
                value={entitySearch}
                onChange={(e) => setEntitySearch(e.target.value)}
                placeholder="Enter entity ID (UUID) to search..."
                className="bg-gray-800 border-gray-700 text-white"
                onKeyPress={(e) => e.key === 'Enter' && searchEntity()}
              />
              <Button onClick={searchEntity} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700">
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            {entityRelationships && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-400">Searching for:</p>
                  <code className="text-cyan-400">{entityRelationships.entity_id}</code>
                </div>

                {entityRelationships.found_in?.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-300 mb-2">Found in Collections:</h3>
                    <div className="space-y-2">
                      {entityRelationships.found_in.map((item, index) => (
                        <div key={index} className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <p className="text-green-400 font-semibold capitalize">{item.collection}</p>
                          <pre className="text-xs text-gray-400 mt-2 overflow-x-auto max-h-40">
                            {JSON.stringify(item.entity, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Entity not found directly in any collection</p>
                )}

                {entityRelationships.references?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-300 mb-2">Referenced By:</h3>
                    <div className="space-y-2">
                      {entityRelationships.references.map((ref, index) => (
                        <div key={index} className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                          <span className="text-blue-400 font-semibold capitalize">{ref.collection}</span>
                          <span className="text-gray-400"> / </span>
                          <code className="text-gray-300">{ref.entity_key}</code>
                          <span className="text-gray-400"> (field: </span>
                          <span className="text-cyan-400">{ref.field}</span>
                          <span className="text-gray-400">)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemAudit;
